import { getAllByIndex, getRecord, idbRequest, putRecord, withOfflineTransaction } from './db';
import { backendUsable, getConnectivitySnapshot, probeConnectivity, subscribeConnectivity } from './connectivity';
import { classifySyncFailure, nextBackoffMs } from './errorClassifier';
import { appendAudit } from './audit';
import { createConflict } from './conflicts';
import {
  notifySyncLeader,
  notifySyncState,
  runAsSyncLeader,
  subscribeSyncWake,
} from './syncLeader';
import type { OutboxCommand } from './types';

export interface SyncAck {
  ack_id: string;
  command_id: string;
  server_resource_id?: string | null;
  server_revision?: string | number | null;
  accepted_at: string;
  result_hash?: string | null;
  status: 'ACKNOWLEDGED';
  resource_type: string;
  resource_id: string;
}

export type SyncHandler = (command: OutboxCommand) => Promise<SyncAck>;
const handlers = new Map<string, SyncHandler>();
let started = false;
let running = false;
let stopLeader: (() => void) | null = null;
const cleanup: Array<() => void> = [];

/*
 * Driver GPS endpoint contract:
 *
 *   server: 5 requests / 10 seconds / authenticated user
 *
 * Keep this client-side guard slightly conservative. The authoritative
 * server limiter remains the final safety boundary.
 *
 * This is intentionally scoped ONLY to driver_gps so high-priority
 * non-GPS outbox commands are not globally throttled.
 */
const GPS_RATE_WINDOW_MS = 10_000;
const GPS_RATE_MAX_REQUESTS = 5;
const GPS_RATE_WINDOW_GUARD_MS = 250;

let gpsRateWindowStartedAt = 0;
let gpsRateWindowAttempts = 0;
let gpsRateWakeTimer: number | null = null;

function reserveGpsRatePermit():
  | { allowed: true }
  | { allowed: false; waitMs: number } {

  const now = Date.now();

  if (
    gpsRateWindowStartedAt === 0 ||
    now - gpsRateWindowStartedAt >= GPS_RATE_WINDOW_MS
  ) {
    gpsRateWindowStartedAt = now;
    gpsRateWindowAttempts = 0;
  }

  if (gpsRateWindowAttempts < GPS_RATE_MAX_REQUESTS) {
    gpsRateWindowAttempts += 1;
    return { allowed: true };
  }

  return {
    allowed: false,
    waitMs: Math.max(
      GPS_RATE_WINDOW_GUARD_MS,
      GPS_RATE_WINDOW_MS -
        (now - gpsRateWindowStartedAt) +
        GPS_RATE_WINDOW_GUARD_MS,
    ),
  };
}

function scheduleGpsRateWake(waitMs: number): void {
  if (gpsRateWakeTimer !== null) return;

  gpsRateWakeTimer = window.setTimeout(() => {
    gpsRateWakeTimer = null;
    notifySyncLeader('DRIVER_GPS_RATE_WINDOW_READY');
  }, Math.max(GPS_RATE_WINDOW_GUARD_MS, waitMs));
}

export function registerSyncHandler(resourceType: string, operation: string, handler: SyncHandler): () => void {
  const key = `${resourceType}:${operation}`;
  handlers.set(key, handler);
  return () => handlers.delete(key);
}

async function updateCommand(command: OutboxCommand, patch: Partial<OutboxCommand>): Promise<OutboxCommand> {
  const updated = { ...command, ...patch };
  await putRecord('outbox_commands', updated);
  return updated;
}

async function dependencySatisfied(command: OutboxCommand): Promise<boolean> {
  if (!command.depends_on_command_id) return true;
  const rows = await getAllByIndex<OutboxCommand>('outbox_commands', 'byCommandId', IDBKeyRange.only(command.depends_on_command_id));
  return rows[0]?.status === 'ACKNOWLEDGED';
}

async function recoverInterruptedSyncingCommands(): Promise<number> {
  const rows = await getAllByIndex<OutboxCommand>(
    'outbox_commands',
    'byStatusPriority',
    IDBKeyRange.bound(
      ['SYNCING', 'P0', 0],
      ['SYNCING', 'P4', Number.MAX_SAFE_INTEGER],
    ),
    100,
  );

  const cutoff = Date.now() - 60_000;
  let recovered = 0;

  for (const command of rows) {
    const startedAt =
      command.last_attempt_at
        ? Date.parse(command.last_attempt_at)
        : 0;

    if (
      !startedAt ||
      !Number.isFinite(startedAt) ||
      startedAt <= cutoff
    ) {
      await updateCommand(command, {
        status: 'READY',
        next_attempt_at: null,
        error_category: 'SYNC',
        error_code: 'INTERRUPTED_SYNC_RECOVERED',
        error_details:
          'Recovered a command left in SYNCING by an interrupted client-side synchronization attempt.',
      });

      recovered += 1;
    }
  }

  return recovered;
}

async function dueCommands(limit = 50): Promise<OutboxCommand[]> {
  const states: OutboxCommand['status'][] = ['READY', 'FAILED_RETRYABLE', 'PENDING'];
  const results: OutboxCommand[] = [];
  for (const state of states) {
    const rows = await getAllByIndex<OutboxCommand>('outbox_commands', 'byStatusPriority', IDBKeyRange.bound([state, 'P0', 0], [state, 'P4', Number.MAX_SAFE_INTEGER]), limit);
    results.push(...rows);
  }
  const now = Date.now();
  return results
    .filter(command => !command.next_attempt_at || Date.parse(command.next_attempt_at) <= now)
    .sort((a, b) => a.priority.localeCompare(b.priority) || a.sequence_no - b.sequence_no)
    .slice(0, limit);
}

async function acknowledge(command: OutboxCommand, ack: SyncAck): Promise<void> {
  await withOfflineTransaction(['outbox_commands', 'sync_acknowledgements', 'domain_records'], 'readwrite', async tx => {
    await idbRequest(tx.objectStore('sync_acknowledgements').put(ack));
    await idbRequest(tx.objectStore('outbox_commands').put({ ...command, status: 'ACKNOWLEDGED', server_ack_id: ack.ack_id, error_category: null, error_code: null, error_details: null, next_attempt_at: null }));
    const localKey = `${command.tenant_id}:${command.user_id}:${command.resource_type}:${command.resource_id}`;
    const domain: any = await idbRequest(tx.objectStore('domain_records').get(localKey));
    if (domain) await idbRequest(tx.objectStore('domain_records').put({ ...domain, sync_status: 'SYNCED', server_revision: ack.server_revision ?? domain.server_revision ?? null, updated_at: new Date().toISOString() }));
  });
  await appendAudit({
    tenant_id: command.tenant_id,
    user_id: command.user_id,
    action: 'SYNC_ACKNOWLEDGED',
    resource_type: command.resource_type,
    resource_id: command.resource_id,
    metadata: {
      command_id: command.command_id,
      ack_id: ack.ack_id,
    },
    sync_status: 'SYNCED',
  });

  /*
   * Publish only after the durable ACK transaction and its audit event
   * have completed. Consumers use this as an invalidation signal and
   * re-read authoritative/local state; the event itself is not state.
   */
  notifySyncState({
    type: 'COMMAND_ACKNOWLEDGED',
    command_id: command.command_id,
    user_id: command.user_id,
    resource_type: command.resource_type,
    resource_id: command.resource_id,
    server_revision: ack.server_revision ?? null,
    accepted_at: ack.accepted_at ?? null,
  });
}

async function processCommand(command: OutboxCommand): Promise<'ACK'|'SKIP'|'RETRYABLE_BLOCK'|'PERMANENT'> {
  if (!(await dependencySatisfied(command))) {
    if (command.status !== 'PENDING') await updateCommand(command, { status: 'PENDING' });
    return 'SKIP';
  }
  const handler = handlers.get(`${command.resource_type}:${command.operation}`);
  if (!handler) {
    await updateCommand(command, { status: 'FAILED_PERMANENT', error_category: 'SYNC', error_code: 'NO_SYNC_HANDLER', error_details: `No sync handler registered for ${command.resource_type}:${command.operation}` });
    return 'PERMANENT';
  }

  const attempt = command.attempt_count + 1;
  command = await updateCommand(command, { status: 'SYNCING', attempt_count: attempt, last_attempt_at: new Date().toISOString(), next_attempt_at: null });
  await appendAudit({ tenant_id: command.tenant_id, user_id: command.user_id, action: 'SYNC_STARTED', resource_type: command.resource_type, resource_id: command.resource_id, metadata: { command_id: command.command_id, attempt }, sync_status: 'SYNCING' });

  try {
    const ack = await handler(command);
    await acknowledge(command, ack);
    localStorage.setItem('coconnect_last_sync_at', new Date().toISOString());
    return 'ACK';
  } catch (error) {
    const failure = classifySyncFailure(error);
    const nextAt = failure.retryable ? new Date(Date.now() + nextBackoffMs(attempt, failure.retryAfterMs)).toISOString() : null;
    const updated = await updateCommand(command, { status: failure.outboxStatus, error_category: failure.category, error_code: failure.code, error_details: failure.message, next_attempt_at: nextAt });
    if (failure.outboxStatus === 'CONFLICT') {
      await createConflict({ tenant_id: command.tenant_id, user_id: command.user_id, resource_type: command.resource_type, resource_id: command.resource_id, local_value: command.payload, server_value: (error as any)?.response?.data, status: 'USER_ACTION_REQUIRED' });
    }
    await appendAudit({ tenant_id: command.tenant_id, user_id: command.user_id, action: 'SYNC_FAILED', resource_type: command.resource_type, resource_id: command.resource_id, metadata: { command_id: command.command_id, category: failure.category, code: failure.code, next_attempt_at: updated.next_attempt_at }, sync_status: 'FAILED' });
    if (failure.retryable || failure.code === 'AUTH_REQUIRED') return 'RETRYABLE_BLOCK';
    return 'PERMANENT';
  }
}

export async function runSyncOnce(): Promise<{ attempted: number }> {
  if (running) return { attempted: 0 };
  running = true;
  try {
    const connectivity = await probeConnectivity();

    if (
      !backendUsable(connectivity.state) ||
      connectivity.authenticated === false
    ) {
      return { attempted: 0 };
    }

    await recoverInterruptedSyncingCommands();

    const commands = await dueCommands();
    let attempted = 0;

    for (const command of commands) {
      /*
       * GPS is governed by a dedicated server rate window.
       *
       * Do not call processCommand() until a local permit is available,
       * because processCommand() increments attempt_count and writes
       * SYNC_STARTED before invoking the HTTP handler.
       *
       * Skipping here therefore does not manufacture false attempts or
       * false audit failures.
       */
      if (command.resource_type === 'driver_gps') {
        const permit = reserveGpsRatePermit();

        if (!permit.allowed) {
          scheduleGpsRateWake(permit.waitMs);
          continue;
        }
      }

      const outcome = await processCommand(command);
      attempted += 1;

      if (outcome === 'RETRYABLE_BLOCK') {
        break;
      }
    }

    return { attempted };
  } finally { running = false; }
}

export async function startSyncCoordinator(): Promise<() => void> {
  if (started) return () => {};
  started = true;
  const leaderLoop = async (signal: AbortSignal) => {
    const wake = () => { if (!signal.aborted) void runSyncOnce(); };
    cleanup.push(subscribeConnectivity(s => { if (backendUsable(s.state)) wake(); }));
    cleanup.push(subscribeSyncWake(() => wake()));
    const interval = window.setInterval(wake, 30000);
    cleanup.push(() => window.clearInterval(interval));
    wake();
    await new Promise<void>(resolve => signal.addEventListener('abort', () => resolve(), { once: true }));
  };
  stopLeader = await runAsSyncLeader(leaderLoop);
  return stopSyncCoordinator;
}

export function stopSyncCoordinator(): void {
  started = false;

  stopLeader?.();
  stopLeader = null;

  if (gpsRateWakeTimer !== null) {
    window.clearTimeout(gpsRateWakeTimer);
    gpsRateWakeTimer = null;
  }

  gpsRateWindowStartedAt = 0;
  gpsRateWindowAttempts = 0;

  cleanup
    .splice(0)
    .forEach(fn => {
      try {
        fn();
      } catch {}
    });
}

export function requestSync(reason = 'MANUAL'): void {
  // All synchronization execution belongs to the elected leader.
  // Wake the leader rather than starting a competing worker in this tab.
  notifySyncLeader(reason);
}
